import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BookingWidget } from "@/components/BookingWidget";
import { useT, useLang } from "@/context/LanguageContext";

// Réservation directe (Stripe) — passée à false pour masquer et renvoyer vers Airbnb.
const DIRECT_BOOKING_ENABLED = true;

export default function ListingDetail() {
  const { slug } = useParams();
  const tl = useT().listing;
  const { lang } = useLang();
  const [l, setL] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setL(null); setNotFound(false);
    api.get(`/listings/${slug}`).then((r) => setL(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-32 text-center px-5">
      <h1 className="font-serif text-4xl">{tl.notFound}</h1>
      <Link to="/collection" className="overline inline-block mt-6 border-b border-ink pb-1">{tl.backLink}</Link>
    </div>
  );
  if (!l) return <div className="pt-40 pb-40 text-center text-ink/40">{tl.loading}</div>;

  const gallery = l.images && l.images.length ? l.images : [l.cover_image];

  return (
    <>
      <Seo title={`${l.name} — ${l.city}`} description={l.short_description} path={`/collection/${l.slug}`} />

      <section className="relative h-[70svh] min-h-[460px] bg-ink overflow-hidden">
        <img src={l.cover_image} alt={`${l.name}, ${l.category} à ${l.city}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-ink/20" />
        <div className="absolute inset-0 max-w-[1500px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-12 text-ivory">
          <Link to="/collection" data-testid="back-to-collection" className="overline inline-flex items-center gap-2 mb-6 opacity-80 hover:opacity-100 transition-opacity">
            <ArrowLeft size={16} /> {tl.back}
          </Link>
          <p className="overline text-gold mb-3">{l.category}</p>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95]">{l.name}</h1>
          <p className="mt-4 inline-flex items-center gap-2 text-ivory/80"><MapPin size={16} /> {l.city}</p>
          {l.rating && l.reviews_count && (
            <p className="mt-3 text-ivory/70 text-sm tracking-wide" data-testid="listing-rating">{lang === "fr" ? String(l.rating).replace(".", ",") : l.rating} · {l.reviews_count} {tl.reviews}</p>
          )}
        </div>
      </section>

      <section className="py-20 sm:py-28 max-w-[1500px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2 space-y-16">
            <Reveal>
              <p className="overline text-ink/50 mb-5">{tl.presentation}</p>
              <p className="font-serif text-3xl sm:text-4xl leading-snug">{l.short_description}</p>
              {l.long_description && <p className="mt-8 text-ink/70 leading-relaxed text-lg">{l.long_description}</p>}
            </Reveal>

            {gallery.length > 0 && (
              <Reveal>
                <p className="overline text-ink/50 mb-6">{tl.gallery}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {gallery.map((img, i) => (
                    <div key={i} className={`img-zoom bg-sand/40 ${i === 0 ? "sm:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}>
                      <img src={img} alt={`${l.name} — ${tl.view} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <aside className="lg:sticky lg:top-28 h-fit space-y-6">
            {DIRECT_BOOKING_ENABLED ? (
              <BookingWidget listing={l} />
            ) : (
              <div className="border border-ink/12 p-8" data-testid="airbnb-booking-box">
                <p className="overline text-ink/50 mb-5">{tl.bookTitle}</p>
                <a href={l.booking_url} target="_blank" rel="noreferrer" data-testid="book-airbnb-btn" className="block text-center bg-ink text-ivory px-8 py-4 overline hover:bg-gold hover:text-ink transition-colors">
                  {tl.book} Airbnb
                </a>
                <p className="text-xs text-ink/50 mt-4 leading-relaxed">{tl.bookNote}</p>
              </div>
            )}
            {(l.features?.length > 0 || l.location) && (
              <div className="border border-ink/12 p-8">
                {l.features && l.features.length > 0 && (
                  <>
                    <p className="overline text-ink/50 mb-5">{tl.features}</p>
                    <ul className="space-y-3 mb-6">
                      {l.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-ink/75"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {f}</li>
                      ))}
                    </ul>
                  </>
                )}
                {l.location && (
                  <>
                    <p className="overline text-ink/50 mb-2">{tl.location}</p>
                    <p className="text-sm text-ink/70 inline-flex items-center gap-2"><MapPin size={15} /> {l.location}</p>
                  </>
                )}
              </div>
            )}
          </aside>
        </div>
      </section>
    </>
  );
}
